import { type FormEventHandler, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { profileApi } from '@entities/profile';
import { showToast } from '@features/ToastManager';

import { PROFILE_TOAST_SCOPE } from './toast';
import {
    mapPasswordFormErrors,
    type PasswordFormErrors,
} from '../lib/mapPasswordFormErrors';

import type { IDataResponseError } from '@shared/model/types/dataResponse.inteface';

type PasswordFormValues = {
    oldPassword: string;
    password: string;
    passwordConfirmation: string;
};

const initialValues: PasswordFormValues = {
    oldPassword: '',
    password: '',
    passwordConfirmation: '',
};

export const usePasswordSettings = () => {
    const { t } = useTranslation();
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState<PasswordFormErrors>({});
    const mutation = useMutation({
        mutationFn: profileApi.updatePassword,
        onSuccess: () => {
            setValues(initialValues);
            setErrors({});
            showToast(t('profile.page.settings.password.success'), {
                scope: PROFILE_TOAST_SCOPE,
            });
        },
        onError: (error) => {
            const responseError = isAxiosError<IDataResponseError>(error)
                ? error.response?.data
                : undefined;
            setErrors({
                ...mapPasswordFormErrors(responseError?.errors),
                form: responseError?.message ?? t('profile.page.settings.password.errors.save'),
            });
        },
    });

    const updateValue = (name: keyof PasswordFormValues, value: string) => {
        setErrors((current) => ({ ...current, [name]: undefined, form: undefined }));
        setValues((current) => ({ ...current, [name]: value }));
    };
    const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();
        setErrors({});
        mutation.mutate({
            'old_password': values.oldPassword,
            password: values.password,
            'password_confirmation': values.passwordConfirmation,
        });
    };

    return { t, values, errors, updateValue, handleSubmit, isPending: mutation.isPending };
};
